import { Injectable } from '@angular/core';
import { RoleConst } from 'src/app/Models/roles.const';
import { WHATS_NEW_RELEASE, WhatsNewRelease, WhatsNewScope } from 'src/app/whats-new.config';

type WhatsNewStorageState = {
  releaseId: string;
  viewCount: number;
};

@Injectable({
  providedIn: 'root',
})
export class WhatsNewService {
  private readonly storagePrefix = 'ewrs.newFeatures.ack';
  /** Minimum number of times the modal is shown before it stops appearing. */
  private readonly minDisplayCount = 2;
  private readonly adminRoleIds = [
    RoleConst.SpaceAdmin,
    RoleConst.UserAdmin,
    RoleConst.ReportAdmin,
    RoleConst.SuperUser,
  ];

  getScope(user: any): WhatsNewScope {
    const roleIds: number[] = user?.userRoles?.map((r: any) => r.roleTypeId) || [];
    if (roleIds.some((id) => this.adminRoleIds.includes(id))) {
      return 'admin';
    } else if (user?.isManager) {
      return 'manager';
    } else {
      return 'user';
    }
  }

  getReleaseFor(scope: WhatsNewScope): WhatsNewRelease {
    return {
      ...WHATS_NEW_RELEASE,
      items: WHATS_NEW_RELEASE.items.filter((item) => item.audiences.includes(scope)),
    };
  }

  shouldShow(userId: number | null | undefined, scope: WhatsNewScope): boolean {
    if (!userId) return false;
    const state = this.readState(userId, scope);
    if (!state || state.releaseId !== WHATS_NEW_RELEASE.id) return true;
    return state.viewCount < this.minDisplayCount;
  }

  /** Records one dismissal; stops showing after {@link minDisplayCount} dismissals. */
  acknowledge(userId: number | null | undefined, scope: WhatsNewScope): void {
    if (!userId) return;
    const state = this.readState(userId, scope);
    const viewCount = state?.releaseId === WHATS_NEW_RELEASE.id ? state.viewCount + 1 : 1;
    this.writeState(userId, scope, { releaseId: WHATS_NEW_RELEASE.id, viewCount });
  }

  private storageKey(userId: number, scope: WhatsNewScope): string {
    return `${this.storagePrefix}.${userId}.${scope}`;
  }

  private readState(userId: number, scope: WhatsNewScope): WhatsNewStorageState | null {
    const raw = localStorage.getItem(this.storageKey(userId, scope));
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as WhatsNewStorageState;
      if (parsed && typeof parsed.releaseId === 'string' && typeof parsed.viewCount === 'number') {
        return parsed;
      }
    } catch {
      // Legacy value: release id string only (single dismissal).
    }

    return { releaseId: raw, viewCount: 1 };
  }

  private writeState(userId: number, scope: WhatsNewScope, state: WhatsNewStorageState): void {
    localStorage.setItem(this.storageKey(userId, scope), JSON.stringify(state));
  }
}
